import { useEffect, useState } from "react";
import type { FC } from "react";
import { Link } from "@tanstack/react-router";
import { Plumb } from "@/components/plumb/Plumb";
import { CASES, CHAPTERS, LINKS, STATEMENT, WORKED_WITH, depthOf } from "./content";
import { useTheme } from "./hooks";

/**
 * Thin bar: mark, two coloured calls to action, the light and dark switch, and
 * the case studies behind a plus. On the studio page (`home`) the mark scrolls
 * to the top; elsewhere it leads back to the studio.
 */
export const Nav: FC<{ home?: boolean }> = ({ home }) => {
  const [open, setOpen] = useState(false);
  const [theme, flip] = useTheme();
  const mark = (
    <>
      <img src="/genie-mark/genie-head-1.svg" alt="" />
      <span>Genie</span>
    </>
  );

  return (
    <nav className="sd-nav" aria-label="Main">
      {home ? (
        <a className="sd-logo" href="#top">
          {mark}
        </a>
      ) : (
        <Link className="sd-logo" to="/">
          {mark}
        </Link>
      )}
      <a className="sd-cta" href={LINKS.app}>
        <span>Create with our Workflows</span>
        <b>+</b>
      </a>
      <Link className="sd-ask" to="/contact">
        <span>Have a project for us?</span>
        <b>→</b>
      </Link>
      <button
        type="button"
        className="sd-theme"
        aria-label={theme === "dark" ? "switch to the light version" : "switch to the dark version"}
        aria-pressed={theme === "light"}
        onClick={flip}
      >
        <svg viewBox="0 0 16 16" aria-hidden>
          <circle cx="8" cy="8" r="6.25" />
          <path d="M8 1.75a6.25 6.25 0 0 1 0 12.5z" />
        </svg>
      </button>
      <div className="sd-nav-links">
        <Link to="/collaborators">Collaborators</Link>
        <div className="sd-menu" data-open={open || undefined} onMouseLeave={() => setOpen(false)}>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}
            onMouseEnter={() => setOpen(true)}
          >
            <span>Case studies</span>
            <b>{open ? "−" : "+"}</b>
          </button>
          <ul>
            {CASES.map(work => (
              <li key={work.id}>
                <Link to="/case/$id" params={{ id: work.id }} onClick={() => setOpen(false)}>
                  <span>{work.title}</span>
                  <span>{work.code}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

/** Who the studio has worked with, as one ruled row. */
export const Shown: FC = () => (
  <section className="sd-shown">
    <p className="sd-label">we worked with</p>
    <ul>
      {WORKED_WITH.map(item => (
        <li key={item.name} aria-label={item.name}>
          <img src={`/logos/${item.logo}.svg`} alt="" />
          <img src={`/logos/${item.logo}-text.svg`} alt={item.name} />
        </li>
      ))}
    </ul>
  </section>
);

/**
 * One statement, then the four things the studio does. The picture is read
 * live for whichever tile is open; the open tile carries what is in it.
 */
export const Statement: FC = () => {
  const [open, setOpen] = useState(0);
  const chapter = CHAPTERS[open];

  useEffect(() => {
    const pick = () => {
      const n = CHAPTERS.findIndex(row => `#${row.id}` === window.location.hash);
      if (n >= 0) setOpen(n);
    };
    pick();
    window.addEventListener("hashchange", pick);
    return () => window.removeEventListener("hashchange", pick);
  }, []);

  return (
    <section className="sd-statement">
      <p className="sd-label">{STATEMENT.label}</p>
      <h2>{STATEMENT.text}</h2>
      <div className="sd-media">
        <div className="sd-media-pic">
          <img className="sd-cover" src={chapter.src} alt="" />
          <Plumb
            key={chapter.id}
            className="sd-cover"
            src={chapter.src}
            depth={depthOf(chapter.src)}
            mode={chapter.mode}
            overlay={chapter.overlay}
            nodes={7}
            tempo={0.3}
          />
          <p className="sd-bench-tag">
            {chapter.no} / {chapter.title}
          </p>
        </div>
        <div className="sd-tiles">
          {CHAPTERS.map((row, n) => (
            <article
              key={row.id}
              id={row.id}
              className="sd-tile"
              data-tone={row.tone}
              data-open={open === n || undefined}
              tabIndex={0}
              onMouseEnter={() => setOpen(n)}
              onFocus={() => setOpen(n)}
              onClick={() => setOpen(n)}
            >
              <h3 className="sd-tile-name">
                <span>{row.no}</span>
                <span>{row.title}</span>
                <b aria-hidden>{open === n ? "−" : "+"}</b>
              </h3>
              <div className="sd-tile-more">
                <div>
                  <p className="sd-tile-head">{row.headline}</p>
                  <p>{row.body}</p>
                  <ul>
                    {row.index.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                    <li>
                      <span className="sd-label">you get</span> {row.out}
                    </li>
                  </ul>
                  <Link className="sd-button" to="/contact" hash={row.id} tabIndex={open === n ? undefined : -1}>
                    <span>start a {row.kind} project</span>
                    <span aria-hidden>→</span>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
